/**
 * Centralized Pricing Cache Service - Countdown Edition
 * All reads/writes to the card_prices table go through this file.
 * No other file should touch card_prices directly.
 *
 * Data source: Countdown API (eBay sold listings)
 * Storage: evidence[] (raw listings) + stats{} (computed per condition/grade)
 */

import { supabase } from '../lib/supabase';
import { fetchPricing, fetchGradedPricing } from '../lib/fetchPricing';
import { isVintageSet } from '../utils/vintageDetection';

const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// ---- Helpers ----

function conditionToKey(grade) {
  if (!grade) return 'nm';
  const g = grade.toLowerCase();
  if (g === 'nm' || g === 'near mint') return 'nm';
  if (g === 'lp' || g === 'lightly played') return 'lp';
  if (g === 'mp' || g === 'moderately played') return 'mp';
  if (g === 'hp' || g === 'heavily played') return 'hp';
  if (g === 'dmg' || g === 'damaged') return 'dmg';
  return 'nm';
}

/**
 * Check if a grade string represents a graded card (e.g. "PSA 10", "CGC 9.5")
 */
function isGradedCard(grade) {
  if (!grade) return false;
  const g = grade.trim().toUpperCase();
  return /^(PSA|CGC|BGS|SGC|ACE|TAG|GMA|MNT)\s/.test(g);
}

export function formatPrice(price) {
  if (price == null) return null;
  if (price < 1) return `$${price.toFixed(2)}`;
  const rounded = Math.round(price * 100) / 100;
  return `$${rounded.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ---- Read Functions ----

/**
 * Bulk read card prices from card_prices table.
 * Computes display price based on user's pricing preferences.
 * @param {Array} cards - [{id, name, set_name, set_number, grade, variant}]
 * @param {Object} [pricingPrefs] - {modern_range, vintage_method}
 * @returns {Map<cardId, {row, price, formatted}>}
 */
export async function getBulkCardPrices(cards, pricingPrefs) {
  if (!cards || cards.length === 0) return new Map();

  const uniqueNames = [...new Set(cards.map(c => c.name).filter(Boolean))];
  if (uniqueNames.length === 0) return new Map();

  console.log('[PriceCache] Bulk read: querying', uniqueNames.length, 'unique names');

  const { data, error } = await supabase
    .from('card_prices')
    .select('*')
    .in('card_name', uniqueNames);

  if (error) {
    console.error('[PriceCache] Bulk read error:', error);
    return new Map();
  }

  console.log('[PriceCache] Bulk read: got', (data || []).length, 'rows from card_prices');

  // Index by card_name + set_name + set_number + variant
  const rowMap = new Map();
  for (const row of (data || [])) {
    const key = `${row.card_name}||${row.set_name || ''}||${row.set_number || ''}||${row.variant || ''}`;
    rowMap.set(key, row);
  }

  const results = new Map();
  for (const card of cards) {
    const v = (card.variant || '').toLowerCase().trim();
    const key = `${card.name}||${card.set_name || ''}||${card.set_number || ''}||${v}`;
    const row = rowMap.get(key);
    if (row) {
      const price = getPriceForCard(row.stats, card.grade, card.set_name, pricingPrefs, row.evidence);
      results.set(card.id, {
        row,
        price,
        formatted: price != null ? formatPrice(price) : null,
      });
    }
  }

  console.log('[PriceCache] Bulk read: matched', results.size, 'cards with prices');
  return results;
}

/**
 * Single row lookup from card_prices.
 */
export async function getCardPriceRow(cardName, setName, setNumber, variant) {
  if (!cardName) return null;

  const { data, error } = await supabase
    .from('card_prices')
    .select('*')
    .eq('card_name', cardName)
    .eq('set_name', setName || '')
    .eq('set_number', setNumber || '')
    .eq('variant', (variant || '').toLowerCase().trim())
    .maybeSingle();

  if (error) {
    console.error('[PriceCache] Single read error:', error);
    return null;
  }
  return data;
}

/**
 * Compute display price from evidence array using user's pricing preferences.
 *
 * Modern cards (2017+): filter evidence by date range (1m/3m/6m/12m), then median.
 * Vintage cards (pre-2017): top3/low3/12mo median/all-time median.
 * Graded cards: always use stats (no date filtering needed, grade-specific).
 *
 * Falls back to precomputed stats if evidence is missing or empty.
 *
 * @param {Object} stats - Precomputed stats from card_prices row
 * @param {string} grade - Card grade/condition
 * @param {string} setName - Set name (for vintage detection)
 * @param {Object} pricingPrefs - {modern_range, vintage_method}
 * @param {Array} evidence - Raw evidence array from card_prices row
 */
/**
 * Get detailed pricing info including fallback grade for graded cards.
 * Returns { price, nearestGrade, nearestPrice, message }
 */
export function getPriceDetails(stats, grade, setName, pricingPrefs, evidence) {
  const price = getPriceForCard(stats, grade, setName, pricingPrefs, evidence);

  if (!isGradedCard(grade)) {
    if (price != null) return { price, nearestGrade: null, nearestPrice: null, message: null };
    if (!stats && (!evidence || evidence.length === 0)) return { price: null, nearestGrade: null, nearestPrice: null, message: 'No sold data found for this card' };
    return { price: null, nearestGrade: null, nearestPrice: null, message: 'No pricing available' };
  }

  // Graded card
  if (price != null) return { price, nearestGrade: null, nearestPrice: null, message: null };

  if (!stats) return { price: null, nearestGrade: null, nearestPrice: null, message: 'No sold data found' };

  // Find closest grade below
  const parts = grade.trim().split(/\s+/);
  const company = parts[0]?.toUpperCase() || '';
  const gradeNum = parseFloat(parts.slice(1).join(' ')) || 0;

  // Collect all grades for this company, sorted descending
  const companyGrades = Object.entries(stats)
    .filter(([key]) => key.toUpperCase().startsWith(company))
    .map(([key, data]) => {
      const num = parseFloat(key.replace(/[^0-9.]/g, '')) || 0;
      return { key, num, data };
    })
    .sort((a, b) => b.num - a.num);

  // Find the closest grade at or below the requested grade
  for (const g of companyGrades) {
    if (g.num <= gradeNum && g.data?.med != null) {
      return {
        price: null,
        nearestGrade: g.key,
        nearestPrice: g.data.med,
        message: `No ${grade} sales found`,
      };
    }
  }

  // Try any grade from this company
  if (companyGrades.length > 0 && companyGrades[0].data?.med != null) {
    return {
      price: null,
      nearestGrade: companyGrades[0].key,
      nearestPrice: companyGrades[0].data.med,
      message: `No ${grade} sales found`,
    };
  }

  return { price: null, nearestGrade: null, nearestPrice: null, message: `No ${company} graded sales found` };
}

export function getPriceForCard(stats, grade, setName, pricingPrefs, evidence) {
  if (!stats && (!evidence || evidence.length === 0)) return null;

  // Graded cards: always use precomputed stats (grade-specific)
  if (isGradedCard(grade)) {
    if (!stats) return null;
    const gradeUpper = grade.trim().toUpperCase();
    if (stats[gradeUpper]?.med != null) return stats[gradeUpper].med;
    if (stats[gradeUpper]?.avg != null) return stats[gradeUpper].avg;
    const gradeLower = grade.trim().toLowerCase();
    if (stats[gradeLower]?.med != null) return stats[gradeLower].med;
    return null;
  }

  // Raw cards: compute from evidence if available
  if (evidence && evidence.length > 0) {
    const vintage = isVintageSet(setName);
    const prefs = pricingPrefs || {};

    if (vintage) {
      return computeVintagePrice(evidence, prefs.vintage_method || 'top3');
    } else {
      return computeModernPrice(evidence, prefs.modern_range || '3m');
    }
  }

  // Fallback to precomputed stats
  if (!stats) return null;
  const allStats = stats.all || stats.raw;
  if (allStats?.med != null) return allStats.med;
  const condKey = conditionToKey(grade);
  if (stats[condKey]?.med != null) return stats[condKey].med;
  const firstKey = Object.keys(stats)[0];
  if (firstKey && stats[firstKey]?.med != null) return stats[firstKey].med;
  return null;
}

// ---- Price Computation from Evidence ----

function getDateCutoff(range) {
  const now = new Date();
  switch (range) {
    case '1m': return new Date(now.setMonth(now.getMonth() - 1));
    case '3m': return new Date(now.setMonth(now.getMonth() - 3));
    case '6m': return new Date(now.setMonth(now.getMonth() - 6));
    case '12m': return new Date(now.setFullYear(now.getFullYear() - 1));
    default: return new Date(now.setMonth(now.getMonth() - 3));
  }
}

function median(prices) {
  if (prices.length === 0) return null;
  const sorted = [...prices].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const val = sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  return Math.round(val * 100) / 100;
}

/**
 * Modern pricing: median of sales within the user's chosen date range.
 */
function computeModernPrice(evidence, range) {
  const cutoff = getDateCutoff(range);
  const prices = evidence
    .filter(e => {
      if (!e.date) return true; // include undated listings (assume recent)
      return new Date(e.date) >= cutoff;
    })
    .map(e => e.price)
    .filter(p => p > 0);

  if (prices.length === 0) {
    // No sales in range, fall back to all evidence
    const allPrices = evidence.map(e => e.price).filter(p => p > 0);
    return median(allPrices);
  }
  return median(prices);
}

/**
 * Vintage pricing based on user's chosen method.
 * - top3: average of 3 highest sales in last 12 months
 * - low3: average of 3 lowest sales in last 12 months
 * - 12avg: median of all sales in last 12 months
 * - alltime: median of all evidence
 */
function computeVintagePrice(evidence, method) {
  const allPrices = evidence.map(e => e.price).filter(p => p > 0);
  if (allPrices.length === 0) return null;

  if (method === 'alltime') {
    return median(allPrices);
  }

  // Filter to last 12 months for other methods
  const cutoff12m = getDateCutoff('12m');
  const recent = evidence
    .filter(e => !e.date || new Date(e.date) >= cutoff12m)
    .map(e => e.price)
    .filter(p => p > 0);

  if (recent.length === 0) return median(allPrices); // fallback

  if (method === '12avg') {
    return median(recent);
  }

  const sorted = [...recent].sort((a, b) => a - b);

  if (method === 'top3') {
    const top = sorted.slice(-3);
    const avg = top.reduce((s, p) => s + p, 0) / top.length;
    return Math.round(avg * 100) / 100;
  }

  if (method === 'low3') {
    const low = sorted.slice(0, 3);
    const avg = low.reduce((s, p) => s + p, 0) / low.length;
    return Math.round(avg * 100) / 100;
  }

  return median(recent);
}

/**
 * Returns true if within 7-day cooldown.
 */
export function isWithinCooldown(lastRefreshedAt) {
  if (!lastRefreshedAt) return false;
  const ts = new Date(lastRefreshedAt).getTime();
  return Date.now() - ts < COOLDOWN_MS;
}

/**
 * Human-readable "X days ago" string.
 */
export function daysSinceRefresh(lastRefreshedAt) {
  if (!lastRefreshedAt) return 'never';
  const ms = Date.now() - new Date(lastRefreshedAt).getTime();
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

// ---- Write Functions ----

/**
 * Upsert one row into card_prices with Countdown data.
 */
export async function upsertCardPrice({
  cardName, setName, setNumber, variant,
  evidence, stats, isGraded, searchQuery,
}) {
  if (!cardName) return;

  const row = {
    card_name: cardName,
    set_name: setName || '',
    set_number: setNumber || '',
    variant: (variant || '').toLowerCase().trim(),
    last_refreshed_at: new Date().toISOString(),
  };

  if (evidence !== undefined) row.evidence = evidence;
  if (stats !== undefined) row.stats = stats;
  if (isGraded !== undefined) row.is_graded = isGraded;
  if (searchQuery !== undefined) row.search_query = searchQuery;

  console.log('[PriceCache] Upserting:', cardName, '|', setName, '|', setNumber, '|', row.variant,
    `evidence:${(evidence || []).length}`, `graded:${isGraded ? 'yes' : 'no'}`);

  const { error } = await supabase
    .from('card_prices')
    .upsert(row, { onConflict: 'card_name,set_name,set_number,variant' });

  if (error) {
    console.error('[PriceCache] Upsert error:', error);
  } else {
    console.log('[PriceCache] Upsert success:', cardName);
  }
}

// ---- Fetch + Cache (Single Card) ----

/**
 * Fetch and cache pricing for a single card. Fire-and-forget friendly.
 */
export async function fetchAndCacheSingleCard({ name, setName, setNumber, variant, grade }) {
  if (!name) return;

  try {
    if (isGradedCard(grade)) {
      const result = await fetchGradedPricing({
        name,
        setName,
        number: setNumber,
        condition: grade,
        variant,
      });
      if (result?.pricingData) {
        await upsertCardPrice({
          cardName: name,
          setName: setName || '',
          setNumber: setNumber || '',
          variant: variant || '',
          evidence: result.pricingData.evidence || [],
          stats: result.pricingData.stats || {},
          isGraded: true,
          searchQuery: result.pricingData.searchQuery || '',
        });
      }
    } else {
      const pricing = await fetchPricing({
        name,
        setName,
        localId: setNumber,
        variant,
      });
      if (pricing) {
        await upsertCardPrice({
          cardName: name,
          setName: setName || '',
          setNumber: setNumber || '',
          variant: variant || '',
          evidence: pricing.evidence || [],
          stats: pricing.stats || {},
          isGraded: false,
          searchQuery: pricing.searchQuery || '',
        });
      }
    }
  } catch (e) {
    console.error('[PriceCache] Single card fetch error:', name, e.message);
  }
}

// ---- Batch Operations ----

/**
 * Refresh all collection prices via Countdown.
 * @param {Array} cards - [{id, name, set_name, set_number, grade, variant}]
 * @param {Function} onProgress - (completed, total) => void
 * @returns {{updated: number, errors: number}}
 */
export async function refreshCollectionPrices(cards, onProgress) {
  if (!cards || cards.length === 0) return { updated: 0, errors: 0 };

  // Deduplicate by name+set_name+set_number+variant
  const seen = new Set();
  const unique = [];
  for (const card of cards) {
    const v = (card.variant || '').toLowerCase().trim();
    const key = `${card.name}||${card.set_name || ''}||${card.set_number || ''}||${v}`;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(card);
    }
  }

  const total = unique.length;
  let completed = 0;
  let updated = 0;
  let errors = 0;

  // Fetch in batches of 5 (Countdown is slower than JustTCG, be gentle)
  const BATCH_SIZE = 5;
  for (let i = 0; i < unique.length; i += BATCH_SIZE) {
    const batch = unique.slice(i, i + BATCH_SIZE);
    const results = await Promise.all(
      batch.map(async (card) => {
        try {
          await fetchAndCacheSingleCard({
            name: card.name,
            setName: card.set_name,
            setNumber: card.set_number,
            variant: card.variant,
            grade: card.grade,
          });
          return true;
        } catch (e) {
          console.error('[PriceCache] Batch fetch error:', card.name, e.message);
          return false;
        }
      })
    );
    completed += batch.length;
    updated += results.filter(Boolean).length;
    errors += results.filter(r => !r).length;
    if (onProgress) onProgress(completed, total);
  }

  return { updated, errors };
}

/**
 * Auto-fetch pricing only for cards with no existing card_prices row.
 */
export async function autoFetchMissingPrices(cards, onProgress) {
  if (!cards || cards.length === 0) return { updated: 0, errors: 0 };

  const existing = await getBulkCardPrices(cards);
  const missing = cards.filter(c => !existing.has(c.id));

  if (missing.length === 0) return { updated: 0, errors: 0 };

  return refreshCollectionPrices(missing, onProgress);
}

// Re-export for backwards compatibility
export { conditionToKey, isGradedCard };
